import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Tab,
  Tabs,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTheme } from "@mui/material";
import { tokens } from "../dashboard/theme";
import { ReactSession } from "react-client-session";
import axios from "axios";
import config from "../pages/global/config";
import PaymentReceipt from "../pages/PaymentReceipt";
import PaymentFilters from "../pages/PaymentFilters";
import DailyPaymentsReport from "../pages/DailyPaymentsReport";
import CloseIcon from "@mui/icons-material/Close";
import FilteredPaymentsReport from "../pages/FilteredPaymentsReport";
import LatePaymentsFilter from "./LatePaymentsFilter";

const PaymentsDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isAdmin = ReactSession.get("role") === "admin";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paidPayments, setPaidPayments] = useState([]);
  const [latePayments, setLatePayments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmedPayment, setConfirmedPayment] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentMode, setPaymentMode] = useState("Espèces");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientError, setClientError] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [filteredReportDialogOpen, setFilteredReportDialogOpen] =
    useState(false);
  const [daysOverdueRange, setDaysOverdueRange] = useState({
    preset: "all",
    min: "",
    max: "",
  });

  const getApiConfig = () => {
    const token = ReactSession.get("token");
    return {
      baseURL: config.apiUrl,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
  };
  const getTodaysPayments = () => {
    const today = new Date().toISOString().split("T")[0];
    return paidPayments.filter(
      (payment) => payment.date_paiement?.split(" ")[0] === today
    );
  };

  const fetchClients = async () => {
    try {
      setIsLoadingClients(true);
      setClientError(null);

      const api = axios.create({
        baseURL: config.apiUrl,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const response = await api.get("client.php");
      if (response.data && Array.isArray(response.data)) {
        setClients(response.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClientError(error.message || "Failed to load clients");
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setIsLoadingClients(false);
    }
  };

  const calculateDaysLate = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };
  const calculatePaymentSummary = (payment) => {
    const allPayments = [...paidPayments, ...pendingPayments].filter(
      (p) => p.id_paiement_tranche === payment.id_paiement_tranche
    );

    const totalAmount =
      parseFloat(payment.montant_total) ||
      allPayments.reduce((sum, p) => sum + parseFloat(p.montant), 0);

    const paidAmount = paidPayments
      .filter((p) => p.id_paiement_tranche === payment.id_paiement_tranche)
      .reduce((sum, p) => sum + parseFloat(p.montant), 0);

    console.log("paidAmoun ::::", paidAmount);

    const remainingAmount = totalAmount - paidAmount;

    const totalPayments = allPayments.length;
    const paidPaymentsCount = paidPayments.filter(
      (p) => p.id_paiement_tranche === payment.id_paiement_tranche
    ).length;
    const remainingPayments = totalPayments - paidPaymentsCount;

    const nextPayment = pendingPayments
      .filter((p) => p.id_paiement_tranche === payment.id_paiement_tranche)
      .sort((a, b) => new Date(a.date_echeance) - new Date(b.date_echeance))[0];

    return {
      totalAmount,
      paidAmount,
      remainingAmount,
      totalPayments,
      paidPaymentsCount,
      remainingPayments,
      nextPayment,
      isComplete: remainingPayments === 0,
    };
  };

  const PaymentSummarySection = ({ payment }) => {
    const summary = calculatePaymentSummary(payment);
    const progressPercentage = (summary.paidAmount / summary.totalAmount) * 100;

    return (
      <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: colors.primary[900] }}>
        <Typography variant="h6" color={colors.grey[100]} gutterBottom>
          Résumé des Paiements
        </Typography>

        {/* Progress Bar */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color={colors.grey[300]}>
              Progression des Paiements
            </Typography>
            <Typography variant="body2" color={colors.grey[300]}>
              {progressPercentage.toFixed(1)}%
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              bgcolor: colors.primary[800],
              borderRadius: 1,
              height: "10px",
            }}
          >
            <Box
              sx={{
                width: `${progressPercentage}%`,
                bgcolor: colors.greenAccent[500],
                height: "100%",
                borderRadius: 1,
                transition: "width 0.5s ease-in-out",
              }}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Payment Amounts */}
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: colors.primary[800], p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" color={colors.grey[300]}>
                Montant Total à Payer
              </Typography>
              <Typography variant="h5" color={colors.grey[100]} sx={{ mt: 1 }}>
                {summary.totalAmount.toLocaleString("fr-FR", {
                  style: "currency",
                  currency: "MAD",
                })}
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: colors.greenAccent[700],
                p: 2,
                borderRadius: 1,
                mt: 2,
              }}
            >
              <Typography variant="subtitle2" color={colors.grey[100]}>
                Total Payé
              </Typography>
              <Typography variant="h5" color={colors.grey[100]} sx={{ mt: 1 }}>
                {summary.paidAmount.toLocaleString("fr-FR", {
                  style: "currency",
                  currency: "MAD",
                })}
              </Typography>
              <Typography variant="caption" color={colors.grey[100]}>
                {summary.paidPaymentsCount} tranche
                {summary.paidPaymentsCount > 1 ? "s" : ""} payée
                {summary.paidPaymentsCount > 1 ? "s" : ""}
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: colors.redAccent[700],
                p: 2,
                borderRadius: 1,
                mt: 2,
              }}
            >
              <Typography variant="subtitle2" color={colors.grey[100]}>
                Reste à Payer
              </Typography>
              <Typography variant="h5" color={colors.grey[100]} sx={{ mt: 1 }}>
                {summary.remainingAmount.toLocaleString("fr-FR", {
                  style: "currency",
                  currency: "MAD",
                })}
              </Typography>
              <Typography variant="caption" color={colors.grey[100]}>
                {summary.remainingPayments} tranche
                {summary.remainingPayments > 1 ? "s" : ""} restante
                {summary.remainingPayments > 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>

          {/* Next Payment Details */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: colors.primary[900],
                p: 2,
                borderRadius: 1,
                height: "100%",
              }}
            >
              <Typography
                variant="subtitle2"
                color={colors.grey[300]}
                gutterBottom
              >
                Détails des Tranches
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Total
                  </Typography>
                  <Typography variant="h6" color={colors.grey[100]}>
                    {summary.totalPayments}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Payées
                  </Typography>
                  <Typography variant="h6" color={colors.greenAccent[400]}>
                    {summary.paidPaymentsCount}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Restantes
                  </Typography>
                  <Typography variant="h6" color={colors.redAccent[400]}>
                    {summary.remainingPayments}
                  </Typography>
                </Grid>
              </Grid>

              {summary.nextPayment && !summary.isComplete && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color={colors.grey[300]}
                    gutterBottom
                  >
                    Prochaine Échéance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[300]}>
                        Date
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        {new Date(
                          summary.nextPayment.date_echeance
                        ).toLocaleDateString("fr-FR")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[300]}>
                        Montant
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        {parseFloat(summary.nextPayment.montant).toLocaleString(
                          "fr-FR",
                          {
                            style: "currency",
                            currency: "MAD",
                          }
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {summary.isComplete && (
          <Box
            mt={3}
            p={2}
            sx={{
              backgroundColor: colors.greenAccent[700],
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CheckCircleOutlineIcon color="inherit" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" color={colors.grey[100]}>
                Paiement Complété
              </Typography>
              <Typography variant="body2" color={colors.grey[200]}>
                Toutes les tranches ont été payées
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const api = axios.create(getApiConfig());

      const queryParams = new URLSearchParams();
      if (selectedClient) {
        queryParams.append("id_client", selectedClient.id_client);
      }
      if (dateRange.start) {
        queryParams.append("date_start", dateRange.start);
      }
      if (dateRange.end) {
        queryParams.append("date_end", dateRange.end);
      }

      const pendingResponse = await api.get(
        `reglements_tranches_clients.php?status=pending&${queryParams.toString()}`
      );
      const processedPendingData = pendingResponse.data.map(processPaymentData);
      setPendingPayments(processedPendingData);

      const paidResponse = await api.get(
        `reglements_tranches_clients.php?status=paid&${queryParams.toString()}`
      );
      const processedPaidData = paidResponse.data.map(processPaymentData);
      setPaidPayments(processedPaidData);

      const lateQueryParams = new URLSearchParams();
      if (selectedClient) {
        lateQueryParams.append("id_client", selectedClient.id_client);
      }
      lateQueryParams.append("tab", "late");

      const lateResponse = await api.get(
        `reglements_tranches_clients.php?status=pending&${lateQueryParams.toString()}`
      );

      let latePaymentsData = lateResponse.data
        .map(processPaymentData)
        .map((payment) => ({
          ...payment,
          jours_retard: calculateDaysLate(payment.date_echeance),
        }));

      latePaymentsData = latePaymentsData.filter((payment) => {
        const daysLate = payment.jours_retard;

        switch (daysOverdueRange.preset) {
          case "all":
            return true;
          case "30":
            return daysLate > 0 && daysLate <= 30;
          case "60":
            return daysLate > 30 && daysLate <= 60;
          case "90":
            return daysLate > 60 && daysLate <= 90;
          case "90+":
            return daysLate > 90;
          case "custom":
            const minDays = parseInt(daysOverdueRange.min) || 0;
            const maxDays = parseInt(daysOverdueRange.max) || Infinity;
            return daysLate >= minDays && daysLate <= maxDays;
          default:
            return true;
        }
      });

      latePaymentsData.sort((a, b) => b.jours_retard - a.jours_retard);
      setLatePayments(latePaymentsData);
      // console.log(latePaymentsData);
    } catch (err) {
      setError("Erreur lors du chargement des paiements: " + err.message);
      console.error("Error fetching payments:", err);
    } finally {
      setLoading(false);
    }
  };
  const processPaymentData = (payment) => ({
    ...payment,
    id: payment.id_reglement,
    montant: payment.montant ? parseFloat(payment.montant) : 0,
    date_echeance: payment.date_echeance || "",
    client: `${payment.nom || ""} ${payment.prenom || ""}`,
  });

  const handlePaymentConfirm = async () => {
    if (!selectedPayment) return;

    try {
      setConfirmLoading(true);
      setError(null);

      const token = ReactSession.get("token");
      const userId = ReactSession.get("idUser");

      const formData = new URLSearchParams();
      formData.append("METHOD", "PUT");
      formData.append(
        "id_paiement_tranche",
        selectedPayment.id_paiement_tranche
      );
      formData.append("numero_tranche", selectedPayment.numero_tranche);
      formData.append("date_paiement", paymentDate);
      formData.append("mode_paiement", paymentMode);
      formData.append("reference_paiement", paymentReference);
      formData.append("statut_reglements", "Payé");
      formData.append("user_upd", userId || "USER");
      formData.append("montant", selectedPayment.montant);
      formData.append("date_echeance", selectedPayment.date_echeance);

      const api = axios.create({
        baseURL: config.apiUrl,
        withCredentials: true,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      });

      await api.post(
        `reglements_tranches_clients.php?id_reglement=${selectedPayment.id_reglement}`,
        formData
      );

      const updatedPayment = {
        ...selectedPayment,
        date_paiement: paymentDate,
        mode_paiement: paymentMode,
        reference_paiement: paymentReference,
        confirmed_by: ReactSession.get("username"),
        confirmed_at: new Date().toLocaleString("fr-FR"),
      };

      setConfirmedPayment(updatedPayment);
      await fetchPayments();
      setConfirmDialogOpen(false);
      setSuccessDialogOpen(true);
    } catch (error) {
      setError("Échec de la confirmation du paiement: " + error.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    setConfirmedPayment(null);
    setPaymentReference("");
    setPaymentMode("Espèces");
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setSelectedPayment(null);
    fetchPayments();
    setActiveTab(0);
  };
  const handleResetFilters = () => {
    setSelectedClient(null);
    setDateRange({ start: "", end: "" });
  };

  useEffect(() => {
    fetchPayments();
  }, [selectedClient, dateRange.start, dateRange.end, daysOverdueRange]);

  useEffect(() => {
    fetchClients();
  }, []);
  const pendingColumns = [
    {
      field: "client",
      headerName: "Client",
      flex: 1.5,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {params.row.nom} {params.row.prenom}
          </Typography>
        </Box>
      ),
    },
    { field: "numero_vente", headerName: "N° Vente", flex: 1 },
    { field: "numero_paiement", headerName: "N° Paiement", flex: 1 },
    { field: "numero_tranche", headerName: "N° Tranche", flex: 1 },
    {
      field: "date_echeance",
      headerName: "Date Échéance",
      flex: 1,
    },
    {
      field: "montant",
      headerName: "Montant",
      flex: 1,
      // valueFormatter: (params) =>
      //   params.value?.toLocaleString("fr-FR", {
      //     style: "currency",
      //     currency: "MAD",
      //   }) || "",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PaymentsIcon />}
          onClick={() => {
            setSelectedPayment(params.row);
            setConfirmDialogOpen(true);
            if (params.row.date_echeance) {
              setPaymentDate(params.row.date_echeance);
            }
          }}
        >
          Confirmer
        </Button>
      ),
    },
  ];

  const paidColumns = [
    {
      field: "client",
      headerName: "Client",
      flex: 1.5,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {params.row.nom} {params.row.prenom}
          </Typography>
        </Box>
      ),
    },
    {
      field: "date_paiement",
      headerName: "Date Paiement",
      flex: 1,
      // valueFormatter: (params) =>
      //   params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "",
    },
    { field: "numero_vente", headerName: "N° Vente", flex: 1 },
    { field: "numero_paiement", headerName: "N° Paiement", flex: 1 },
    { field: "numero_tranche", headerName: "N° Tranche", flex: 1 },
    {
      field: "montant",
      headerName: "Montant",
      flex: 1,
      // valueFormatter: (params) =>
      //   params.value?.toLocaleString("fr-FR", {
      //     style: "currency",
      //     currency: "MAD",
      //   }) || "",
    },
    { field: "mode_paiement", headerName: "Mode Paiement", flex: 1 },
    { field: "reference_paiement", headerName: "Référence", flex: 1 },
  ];

  const lateColumns = [
    {
      field: "client",
      headerName: "Client",
      flex: 1.4,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {params.row.nom} {params.row.prenom}
          </Typography>
        </Box>
      ),
    },
    {
      field: "telephone",
      headerName: "Téléphone",
      flex: 1.3,
    },
    { field: "numero_vente", headerName: "N° Vente", flex: 1 },
    { field: "numero_paiement", headerName: "N° Paiement", flex: 1 },
    { field: "numero_tranche", headerName: "N° Tranche", flex: 1 },
    {
      field: "date_echeance",
      headerName: "Date Échéance",
      flex: 1,
      // valueFormatter: (params) =>
      //   params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "",
    },
    {
      field: "montant",
      headerName: "Montant",
      flex: 1,
      // valueFormatter: (params) =>
      //   params.value?.toLocaleString("fr-FR", {
      //     style: "currency",
      //     currency: "MAD",
      //   }) || "",
    },
    {
      field: "solde_restant",
      headerName: "Reste à Payer",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography color={colors.redAccent[400]}>
            {params.value.toLocaleString("fr-FR", {
              style: "currency",
              currency: "MAD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        );
      },
    },
    {
      field: "jours_retard",
      headerName: "Jours de Retard",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: params.value > 30 ? "#d32f2f" : "#ed6c02",
          }}
        >
          <ErrorOutlineIcon fontSize="small" />
          <Typography>{params.value} jours</Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PaymentsIcon />}
          onClick={() => {
            setSelectedPayment(params.row);
            setConfirmDialogOpen(true);
            if (params.row.date_echeance) {
              setPaymentDate(params.row.date_echeance);
            }
          }}
        >
          Confirmer
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px">
      <PaymentFilters
        activeTab={activeTab}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
        clients={clients}
        onResetFilters={handleResetFilters}
        colors={colors}
      />
      <Paper
        elevation={3}
        sx={{ backgroundColor: colors.primary[900], p: 3, mb: 3 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold">
            Gestion des Paiements
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ReceiptLongIcon />}
            onClick={() => setReportDialogOpen(true)}
          >
            Rapport Journalier
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ReceiptLongIcon />}
            onClick={() => setFilteredReportDialogOpen(true)}
            disabled={!selectedClient && !dateRange.start && !dateRange.end}
          >
            Rapport Filtré
          </Button>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            if (newValue !== 2) {
              setDaysOverdueRange({
                preset: "all",
                min: "",
                max: "",
              });
            }
          }}
          sx={{
            "& .MuiTab-root": {
              color: colors.grey[300],
              "&.Mui-selected": {
                color: colors.greenAccent[400],
              },
            },
            borderBottom: `1px solid ${colors.grey[700]}`,
          }}
        >
          <Tab
            icon={<PaymentsIcon />}
            iconPosition="start"
            label="Paiements en Attente"
          />
          <Tab
            icon={<ReceiptLongIcon />}
            iconPosition="start"
            label="Paiements Effectués"
          />
          <Tab
            icon={<ErrorOutlineIcon />}
            iconPosition="start"
            label="Paiements en Retard"
          />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <TabPanel value={activeTab} index={0}>
            <Box height="65vh">
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={pendingPayments}
                  columns={pendingColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.blueAccent[700],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.secondary,
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                      color: `${colors.grey[100]} !important`,
                    },
                  }}
                />
              )}
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box height="65vh">
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={paidPayments}
                  columns={paidColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.blueAccent[700],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.secondary,
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                      color: `${colors.grey[100]} !important`,
                    },
                  }}
                />
              )}
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <LatePaymentsFilter
              daysOverdueRange={daysOverdueRange}
              setDaysOverdueRange={setDaysOverdueRange}
              colors={colors}
              payments={latePayments}
              companyInfo={{
                name: "Your Company Name",
                address: "123, Avenue Mohammed V, Casablanca",
                phone: "+212 5XX-XXXXXX",
                email: "contact@company.com",
              }}
            />
            <Box height="65vh">
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={latePayments}
                  columns={lateColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.blueAccent[700],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.secondary,
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                      color: `${colors.grey[100]} !important`,
                    },
                  }}
                />
              )}
            </Box>
          </TabPanel>
        </Box>
      </Paper>
      {/* Payment Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !confirmLoading && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentsIcon />
            <Typography variant="h6">Confirmer le Paiement</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedPayment && (
            <PaymentSummarySection payment={selectedPayment} />
          )}
          <Grid container spacing={2}>
            {isAdmin && (
              <Grid item xs={12}>
                <TextField
                  type="date"
                  label="Date de Paiement"
                  fullWidth
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  disabled={confirmLoading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                select
                label="Mode de Paiement"
                fullWidth
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                disabled={confirmLoading}
              >
                <option value="Espèces">Espèces</option>
                <option value="Chèque">Chèque</option>
                <option value="Virement">Virement</option>
                <option value="Carte">Carte</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Référence du Paiement"
                fullWidth
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                disabled={confirmLoading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={confirmLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handlePaymentConfirm}
            variant="contained"
            color="secondary"
            disabled={confirmLoading}
            startIcon={confirmLoading ? <CircularProgress size={20} /> : null}
          >
            {confirmLoading ? "Confirmation..." : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleOutlineIcon color="success" />
            <Typography variant="h6">Paiement Confirmé avec Succès</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {confirmedPayment && (
            <Box mt={2}>
              <PaymentSummarySection payment={confirmedPayment} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Box display="flex" gap={2} justifyContent="flex-end" width="100%">
            {confirmedPayment && (
              <PaymentReceipt
                payment={confirmedPayment}
                paymentSummary={calculatePaymentSummary(confirmedPayment)}
                companyInfo={{
                  name: "Your Company Name",
                  address: "123, Avenue Mohammed V, Casablanca",
                  phone: "+212 5XX-XXXXXX",
                  email: "contact@company.com",
                }}
              />
            )}
            <Button
              onClick={handleSuccessClose}
              variant="contained"
              color="secondary"
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ minWidth: 120 }}
            >
              Terminer
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "230mm",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">
              Rapport des Paiements Journaliers
            </Typography>
            <IconButton onClick={() => setReportDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DailyPaymentsReport
            payments={getTodaysPayments()}
            companyInfo={{
              name: "Your Company Name",
              address: "123, Avenue Mohammed V, Casablanca",
              phone: "+212 5XX-XXXXXX",
              email: "contact@company.com",
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={filteredReportDialogOpen}
        onClose={() => setFilteredReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Rapport des Paiements Filtrés</Typography>
            <IconButton
              onClick={() => setFilteredReportDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <FilteredPaymentsReport
            payments={paidPayments}
            filterInfo={{
              client: selectedClient,
              dateRange: dateRange,
            }}
            onClose={() => setFilteredReportDialogOpen(false)}
            companyInfo={{
              name: "Your Company Name",
              address: "123, Avenue Mohammed V, Casablanca",
              phone: "+212 5XX-XXXXXX",
              email: "contact@company.com",
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

export default PaymentsDashboard;
